/**
 * Prefill the patient's own name on intake signature pages.
 *
 * Veronica Cardona 2026-07-28: "each page is asking for patients to input
 * their name multiple times instead of 1x." A PI patient types their own
 * name at least five times across demographics, the lien, the billing
 * agreement and any hardship or cash-plan form.
 *
 * Those fields do NOT get deleted. A printed name beside a signature is
 * what makes a lien and an assignment of benefits enforceable, so the
 * artifact stays and only the typing goes away. The value is prefilled and
 * remains editable — a patient who signs under a different legal name than
 * the one on file can still correct it.
 *
 * Only the PATIENT's own name is prefilled. Attorney, provider, adjuster,
 * paralegal, emergency contact, subscriber, responsible party and employer
 * names are all different people and are deliberately left blank.
 */

// Field ids belonging to someone other than the patient. Checked as a
// prefix so attorney_printed_name and attorney_name_lien both fall out.
const OTHER_PARTY_PREFIXES = [
  'attorney', 'provider', 'adjuster', 'paralegal', 'ec', 'emergency',
  'subscriber', 'responsible', 'employer', 'er', 'guarantor', 'witness',
  'referring', 'physician', 'parent', 'guardian',
];

/**
 * True when this field is asking for the patient's own printed legal name.
 *
 * Matches on the label rather than the id alone: the real templates use
 * hardship_printed_name and cdp_printed_name, which carry no "patient"
 * prefix but are labelled "Patient Printed Name". The id prefix check then
 * removes the other-party fields that share the same label shape.
 */
export function isPatientNameField(field) {
  if (!field || field.type !== 'text') return false;

  const id = String(field.id || '').toLowerCase();
  const label = String(field.label || '').toLowerCase();

  const prefix = id.split('_')[0];
  if (OTHER_PARTY_PREFIXES.includes(prefix)) return false;

  // A printed-name or legal-name field, labelled as the patient's.
  const looksLikeNameField = /(printed_name|name_lien)$/.test(id);
  const labelledPatient = label.startsWith('patient') && label.includes('name');

  return looksLikeNameField && labelledPatient;
}

/**
 * The patient's full legal name as it should appear above a signature.
 * Returns '' when we don't have both halves — a half-filled legal name is
 * worse than an empty box the patient completes themselves.
 */
export function patientLegalName(patient) {
  const first = String(patient?.first_name || '').trim();
  const last = String(patient?.last_name || '').trim();
  if (!first || !last) return '';
  return `${first} ${last}`;
}

/**
 * Build { fieldId: value } for every patient-name field in `fields`.
 *
 * Returns only the prefillable entries, so callers can spread it UNDER the
 * patient's own answers and never clobber something they typed:
 *   { ...prefillPatientName(fields, patient), ...responses }
 */
export function prefillPatientName(fields, patient) {
  const name = patientLegalName(patient);
  if (!name) return {};

  const out = {};
  (Array.isArray(fields) ? fields : []).forEach((f) => {
    if (isPatientNameField(f)) out[f.id] = name;
  });
  return out;
}
